import React, { useState, useEffect, useCallback, useContext } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon, ArrowLeftIcon } from "@radix-ui/react-icons";
import ExposeCard from "./ExposeCard";
import useEmblaCarousel from "embla-carousel-react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { FavoritesContext } from "../../lib/FavoritesContext";
import config from "@/config/config";
import { cn } from "@/lib/utils";
import { useTrackingFunctions } from "@/lib/meta/TrackingFunctions";

export default function ImmobilienAngebot({ className, classHide, title }) {
  const { id } = useParams();
  const [visibleItems, setVisibleItems] = useState(3);
  const [estates, setEstates] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    skipSnaps: false,
    containScroll: "trim",
  });
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();
  const { favorites, handleFavoriteToggle } = useContext(FavoritesContext); // Get favorites and toggle function from context

  const { trackRelatedExposeClick } = useTrackingFunctions();

  useEffect(() => {
    const fetchEstates = async () => {
      try {
        const response = await axios.get(
          `${config.apiUrl}/api/estates?minPrice=0&maxPrice=1000000&location=&locationRadius=0&minBedrooms=0&maxBedrooms=100&minBathrooms=0&maxBathrooms=100&propertyType=&propertySubtype=&minLotSize=0&maxLotSize=10000000&minLivingSpace=0&maxLivingSpace=50000&minBuildYear=1700&maxBuildYear=2030&offer_type=BOTH`,
          {}
        );
        setEstates(response.data.data);
      } catch (error) {
        console.error("Error fetching estates:", error);
      }
    };
    fetchEstates();
  }, []);

  const handleCardClick = async (id) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/estates/${id}`);
    } catch (error) {
      console.error("Error fetching estate details:", error);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setVisibleItems(4);
      } else if (window.innerWidth >= 1000) {
        setVisibleItems(3);
      } else if (window.innerWidth >= 720) {
        setVisibleItems(2);
      } else {
        setVisibleItems(1);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCurrentPage(Math.floor(emblaApi.selectedScrollSnap() / visibleItems));
    };
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, visibleItems]);

  const totalPages = Math.ceil(estates.length / visibleItems);

  const scrollByPage = useCallback(
    (direction) => {
      if (!emblaApi) return;
      const nextIndex =
        emblaApi.selectedScrollSnap() + direction * visibleItems;
      emblaApi.scrollTo(nextIndex);
    },
    [emblaApi, visibleItems]
  );

  const scrollToPage = (pageIndex) => {
    if (emblaApi) emblaApi.scrollTo(pageIndex * visibleItems); // Scroll to the selected page
  };
  return (
    <div
      className={cn(
        "px-2 max-w-7xl mx-auto py-10 md:py-20 flex flex-col items-center",
        className
      )}
    >
      <div
        className={cn(
          "flex flex-col md:flex-row md:items-center justify-start gap-2 pb-5",
          classHide
        )}
      >
        <h2 className="font-semibold text-prime-950 text-2xl md:text-3xl font-serif self-center text-center w-full md:pb-10">
          {title || "Aktuelles Immobilienangebot"}
        </h2>
      </div>
      <div className="mt-8 w-full relative  overflow-hidden">
        <div ref={emblaRef} className="  w-full">
          <div className="flex gap-5">
            {estates.map((estate, index) => (
              <div
                className="flex-shrink-0   translate-x-2 md:translate-x-0"
                style={{ width: `calc(95% / ${visibleItems})` }}
                key={index}
                onClick={() =>
                  trackRelatedExposeClick(estate.id, estate.title.value)
                }
              >
                <ExposeCard
                  image={
                    estate.images && estate.images.length > 0
                      ? estate.images[0].medium
                      : "Haus.jpeg"
                  }
                  id={estate.id}
                  price={estate.price}
                  location={`${estate.zip_code}, ${estate.city}`}
                  details={`${estate.number_of_rooms} Zimmer, ${estate.living_space} m²`}
                  description={estate.title}
                  base_rent={estate.base_rent}
                  marketing_type={estate.marketing_type}
                  onClick={() => handleCardClick(estate.id)}
                  isFavorited={favorites.some((fav) => fav.id === estate.id)} // Check if the estate is in favorites
                  onFavoriteToggle={() => handleFavoriteToggle(estate)} // Toggle favorite status
                  className="  transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 py-7 justify-center">
          <button
            onClick={() => scrollByPage(-1)}
            className="p-3 border rounded hover:bg-slate-100"
          >
            <ArrowLeftIcon />
          </button>
          <div className="flex gap-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToPage(index)}
                className={`w-[0.6rem] h-[0.6rem] rounded-full transition-all duration-150 ${
                  index === currentPage
                    ? "bg-prime-950 scale-125"
                    : "bg-gray-400 hover:bg-gray-300"
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => scrollByPage(1)}
            className="p-3 border rounded hover:bg-slate-100"
          >
            <ArrowRightIcon />
          </button>
        </div>
      </div>
      <Button
        onClick={() => navigate("/immobilien")}
        className="group hover:bg-transparent font-serif bg-transparent md:text-xl text-md text-prime-950 font-normal rounded-none shadow-none underline underline-offset-4 hover:underline-offset-2  hover:duration-300 hover:transition-all "
      >
        Zum gesamten Immobilienangebot{" "}
        <ArrowRightIcon className="group-hover:-rotate-90 group-hover:transition-transform" />
      </Button>
    </div>
  );
}
